import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { X } from "lucide-react";
import { useStyleNameValidation } from "@/hooks/useStyleNameValidation";
import { useStyleNameSuggestions } from "@/hooks/useStyleNameSuggestions";
import { ValidationIndicator } from "./Bulk_Create/ValidationIndicator";
import { SuggestionChips } from "./Bulk_Create/SuggestionChips";
import { StyleNumberInput } from "./Bulk_Create/StyleNumberInput";
import { TagSelector } from "./Bulk_Create/TagSelector";
import { NameSuggestion, StyleTag } from "@/types";

interface BulkStyleRowData {
  id: string;
  name: string;
  styleNumber: string;
  images: File[];
  tags: StyleTag[];
}

interface BulkStyleRowProps {
  data: BulkStyleRowData;
  index: number;
  onUpdate: <K extends keyof BulkStyleRowData>(
    field: K,
    value: BulkStyleRowData[K]
  ) => void;
  onRemove: () => void;
}

export const BulkStyleRow = ({
  data,
  index,
  onUpdate,
  onRemove,
}: BulkStyleRowProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showNameSuggestions, setShowNameSuggestions] = useState(false);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  const { validation, isValidating, validateStyleName, clearValidation } =
    useStyleNameValidation();
  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    fetchSuggestions,
    clearSuggestions,
  } = useStyleNameSuggestions();

  console.log("data.images", data.images);

  useEffect(() => {
    setImagePreviews(data.images.map((file) => URL.createObjectURL(file)));

    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [data.images]);

  const handleDrop = (files: File[]) => {
    const existingNames = data.images.map((f) => f.name + f.size);
    const newFiles = files.filter(
      (f) => !existingNames.includes(f.name + f.size)
    );
    if (newFiles.length > 0) {
      onUpdate("images", [...data.images, ...newFiles]);
    }
  };

  // const handleDragEnd = (fromIndex: number, toIndex: number) => {
  //   if (fromIndex === toIndex) return;
  //   const newImages = [...data.images];
  //   const [moved] = newImages.splice(fromIndex, 1);
  //   newImages.splice(toIndex, 0, moved);

  //   onUpdate("images", newImages);
  // };

  const handleRemove = (index: number) => {
    const newImages = data.images.filter((_, i) => i !== index);
    onUpdate("images", newImages);
  };

  // Name input handlers with validation and suggestions
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    onUpdate("name", value);

    if (value.trim()) {
      validateStyleName(value);
      fetchSuggestions(value);
      setShowNameSuggestions(true);
    } else {
      clearValidation();
      clearSuggestions();
      setShowNameSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: NameSuggestion) => {
    onUpdate("name", suggestion.name);
    clearSuggestions();
    setShowNameSuggestions(false);
  };

  const handleNameBlur = () => {
    setTimeout(() => {
      setShowNameSuggestions(false);
      clearSuggestions();
    }, 200);
  };

  // Style number handler
  const handleStyleNumberChange = (value: string) => {
    onUpdate("styleNumber", value);
  };

  // Tags handler
  const handleTagsChange = (tags: StyleTag[]) => {
    onUpdate("tags", tags);
  };

  // Image handlers with drag and drop support
  // const handleImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = Array.from(e.target.files || []);
  //   const newImages = [...data.images, ...files];
  //   onUpdate("images", newImages);
  // };

  // const removeImage = (indexToRemove: number) => {
  //   const newImages = data.images.filter((_, i) => i !== indexToRemove);
  //   onUpdate("images", newImages);
  // };

  // const moveImage = (fromIndex: number, toIndex: number) => {
  //   const newImages = [...data.images];
  //   const [movedImage] = newImages.splice(fromIndex, 1);
  //   newImages.splice(toIndex, 0, movedImage);
  //   onUpdate("images", newImages);
  // };

  // DnD-kit sensors
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor)
  );

  // Sortable image thumb
  const SortableImageThumb = ({
    id,
    preview,
    isCover,
    onRemove,
  }: {
    id: string;
    preview: string;
    isCover: boolean;
    onRemove: () => void;
  }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      width: 80,
      height: 80,
      borderRadius: 4,
      overflow: "hidden",
      border: "1px solid #eee",
      position: "relative" as const,
      marginRight: 4,
      background: "#fff",
      display: "inline-block",
    };
    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <img
          src={preview}
          alt="img"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
        {isCover && (
          <span
            style={{
              position: "absolute",
              top: 2,
              left: 2,
              background: "#007bff",
              color: "#fff",
              fontSize: 9,
              borderRadius: 2,
              padding: "0 2px",
            }}
          >
            Cover
          </span>
        )}
        <button
          type="button"
          style={{
            position: "absolute",
            top: 2,
            right: 2,
            background: "#fff",
            border: "none",
            borderRadius: "50%",
            width: 16,
            height: 16,
            cursor: "pointer",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          title="Remove"
        >
          <X size={10} />
        </button>
      </div>
    );
  };

  return (
    <tr className="align-baseline">
      <td className="row-number align-baseline">{index + 1}</td>
      <td className="name-cell">
        <div className="form-group relative">
          <input
            type="text"
            value={data.name}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            placeholder="Style name"
            className={`table-input ${
              validation ? (validation.isValid ? "valid" : "invalid") : ""
            }`}
          />
          <ValidationIndicator
            validation={validation}
            isValidating={isValidating}
          />
          {showNameSuggestions && (
            <div className="suggestions-container-bulk">
              <SuggestionChips
                suggestions={suggestions}
                isLoading={isSuggestionsLoading}
                onSuggestionClick={handleSuggestionClick}
              />
            </div>
          )}
        </div>
      </td>
      <td className="style-number-cell">
        <StyleNumberInput
          value={data.styleNumber}
          onChange={handleStyleNumberChange}
        />
      </td>
      <td className="tags-cell">
        <TagSelector selectedTags={data.tags} onChange={handleTagsChange} />
      </td>
      <td className="images-cell">
        <div
          className="bulk-image-dropzone"
          style={{
            minWidth: 120,
            width: "100%",
            minHeight: 60,
            border: "1px dashed #bbb",
            borderRadius: 6,
            padding: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
            flexWrap: "wrap",
            background: "#fafbfc",
            position: "relative",
            textAlign: "center",
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files).filter((f) =>
              f.type.startsWith("image/")
            );
            handleDrop(files);
          }}
          onClick={() => fileInputRef.current?.click()}
          title="Click or drag images here"
        >
          {imagePreviews.length === 0 && (
            <span style={{ color: "#888", fontSize: 12, width: "100%" }}>
              Drop or click to add
            </span>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (active.id !== over?.id) {
                const oldIndex = data.images.findIndex(
                  (f) => f.name + f.size === active.id
                );
                const newIndex = data.images.findIndex(
                  (f) => f.name + f.size === over?.id
                );
                if (oldIndex !== -1 && newIndex !== -1) {
                  const newImages = arrayMove(data.images, oldIndex, newIndex);
                  onUpdate("images", newImages);
                }
              }
            }}
          >
            <SortableContext
              items={data.images.map((f) => f.name + f.size)}
              strategy={horizontalListSortingStrategy}
            >
              {imagePreviews.map((preview, idx) => (
                <SortableImageThumb
                  key={data.images[idx]?.name + data.images[idx]?.size}
                  id={data.images[idx]?.name + data.images[idx]?.size}
                  preview={preview}
                  isCover={idx === 0}
                  onRemove={() => handleRemove(idx)}
                />
              ))}
            </SortableContext>
          </DndContext>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              if (e.target.files) handleDrop(Array.from(e.target.files));
            }}
          />
        </div>
      </td>
      <td className="actions-cell">
        <button
          type="button"
          className="remove-row-btn"
          onClick={onRemove}
          aria-label="Remove row"
        >
          Delete
        </button>
      </td>
    </tr>
  );
};
